import API from './api';

// ResearchOS — external stock scorecards. Reads are access-scoped server-side
// (superuser → all; linked user → their team; unlinked → none). Admin endpoints are
// superuser-only. Ingest itself is done by the external tool with a per-scorer code,
// never from this UI.
export const researchosService = {
  getScope: () => API.get('/api/researchos/scope/'),
  getHome: () => API.get('/api/researchos/home/'),   // the landing's live tiles (one headline per module)
  // Backfill request→approve workflow: quorum users submit + track; superusers decide.
  getBackfillRequests: () => API.get('/api/research/backfill-requests/'),
  submitBackfillRequest: (payload) => API.post('/api/research/backfill-requests/', payload),
  decideBackfillRequest: (id, action, reason) =>
    API.post(`/api/research/backfill-requests/${id}/decide/`, { action, reason }),
  // Stop a running/queued backfill (resume later via retry — everything fetched is kept).
  stopBackfillRun: (id) => API.post(`/api/research/admin/runs/${id}/stop/`),
  getScores: (params = {}) => API.get('/api/researchos/scores/', { params }),
  // Quorum coverage by sector (latest scorecard per security, grouped by FMP sector) — the
  // sector master tile. Only non-empty sectors returned; blank-sector names counted as uncovered.
  getSectorCoverage: () => API.get('/api/researchos/sector-coverage/'),
  // Per-company profile (search → render): access-scoped scores + latest notebook primer +
  // aggregate public chatter for one ticker. 404 when the ticker is unknown to the platform.
  getCompanyProfile: (ticker) => API.get(`/api/researchos/company/${encodeURIComponent(ticker)}/`),

  // Courier — daily briefs (superuser only; ingest is done by the producer with its token)
  getBriefs: (params = {}) => API.get('/api/researchos/courier/briefs/', { params }),
  getBriefHtml: (id) => API.get(`/api/researchos/courier/briefs/${id}/html/`, { responseType: 'text', transformResponse: [(d) => d] }),

  // admin (superuser only)
  getScorers: () => API.get('/api/researchos/admin/scorers/'),
  createScorer: (data) => API.post('/api/researchos/admin/scorers/', data),
  updateScorer: (id, data) => API.patch(`/api/researchos/admin/scorers/${id}/`, data),
  // per-scorer ingest tokens (mint returns the plaintext code ONCE)
  getScorerTokens: (id) => API.get(`/api/researchos/admin/scorers/${id}/tokens/`),
  mintScorerToken: (id, data) => API.post(`/api/researchos/admin/scorers/${id}/tokens/`, data),
  revokeToken: (id) => API.post(`/api/researchos/admin/tokens/${id}/revoke/`),
  getLinks: () => API.get('/api/researchos/admin/links/'),
  createLink: (data) => API.post('/api/researchos/admin/links/', data),
  deleteLink: (id) => API.delete(`/api/researchos/admin/links/${id}/`),
  getParameters: (params = {}) => API.get('/api/researchos/admin/parameters/', { params }),
  reviewParameter: (id, action) =>
    API.post(`/api/researchos/admin/parameters/${id}/review/`, { action }),

  // Ingested-transcript browser (superuser only) — part of ResearchOS.
  // List returns METADATA only (no raw_text); detail loads the full transcript on demand.
  getIngestDocuments: (params = {}) => API.get('/api/research/admin/documents/', { params }),
  getIngestDocument: (id) => API.get(`/api/research/documents/${id}/`),

  // Backfill control (superuser only)
  triggerBackfill: (data) => API.post('/api/research/admin/backfill/', data),
  // kind: 'youtube_backfill' (default) | 'x_backfill' — each panel lists only its own runs
  getBackfillRuns: (kind) => API.get('/api/research/admin/runs/', { params: kind ? { kind } : {} }),
  // X (official API, wish #6): per-handle backfill + the $0.01 preview + the tracked-handle table
  triggerXBackfill: (data) => API.post('/api/research/admin/x/backfill/', data),
  getXPreview: (handle) => API.get('/api/research/admin/x/preview/', { params: { handle } }),
  getXHandles: () => API.get('/api/research/admin/x/handles/'),
  getXUsage: () => API.get('/api/research/admin/x/usage/'),
  // Reddit (wish #13): the platform-owned worklist the Mac mini's cron reads — edited here, never fetched server-side
  getCollectors: () => API.get('/api/research/admin/collectors/'),   // wish #22: heartbeat rows
  getRedditSources: () => API.get('/api/research/admin/reddit/sources/'),
  addRedditSource: (data) => API.post('/api/research/admin/reddit/sources/', data),
  updateRedditSource: (id, data) => API.patch(`/api/research/admin/reddit/sources/${id}/`, data),
  getWebshareUsage: () => API.get('/api/research/admin/webshare-usage/'),

  // Channel-level coverage (superuser only) — DB-derived, distinct from per-run stats
  getChannels: () => API.get('/api/research/admin/channels/'),
  getChannelCoverage: (params = {}) => API.get('/api/research/admin/channel-coverage/', { params }),

  // Channel relevance (superuser only): user-declared GICS sectors/sub-industries/tickers per channel
  getGicsTaxonomy: () => API.get('/api/research/admin/gics-taxonomy/'),
  getChannelRelevance: (channelId) =>
    API.get('/api/research/admin/channel-relevance/', { params: { channel_id: channelId } }),
  listChannelRelevance: () => API.get('/api/research/admin/channel-relevance/'),  // channels with relevance set
  saveChannelRelevance: (data) => API.post('/api/research/admin/channel-relevance/', data),
  // official-channel declaration (#1c) — the authoritative channel->company mapping.
  getOfficialChannels: () => API.get('/api/research/admin/official-channel/'),
  setOfficialChannel: (data) => API.post('/api/research/admin/official-channel/', data),
  searchEntities: (q) => API.get('/api/research/admin/entity-search/', { params: { q } }),

  // Theme taxonomy (Track A) — buckets + members + pool
  getThemes: () => API.get('/api/research/admin/themes/'),

  // Daily auto-fetch monitor (superuser only)
  getDailyRefresh: () => API.get('/api/research/admin/daily-refresh/'),
  triggerDailyRefresh: () => API.post('/api/research/admin/daily-refresh/'),

  // Extraction: preview (cache-only) → review → explicit commit. Nothing hits the DB until commit.
  startExtraction: (docId) => API.post('/api/research/admin/extract/preview/', { doc_id: docId }),
  pollExtraction: (jobId) => API.get(`/api/research/admin/extract/preview/${jobId}/`),
  commitExtraction: (jobId) => API.post(`/api/research/admin/extract/commit/${jobId}/`),
  // Bulk extract + auto-commit (earliest-first, grounding-gated — no per-video review).
  startBulkExtraction: (body) => API.post('/api/research/admin/extract/bulk/', body),
  pollBulkExtraction: (jobId) => API.get(`/api/research/admin/extract/bulk/${jobId}/`),
  getExtractionResults: (params = {}) => API.get('/api/research/admin/extract/results/', { params }),
  getExtractionResult: (id) => API.get(`/api/research/admin/extract/results/${id}/`),
  getResearchOverview: () => API.get('/api/research/admin/overview/'),
  // New Chatter (revamp phase 1, superuser-only): home aggregates, the mixed feed, one company's main rail + price
  getChatterHome: (params) => API.get('/api/research/admin/chatter/home/', { params }),
  getChatterFeed: (params) => API.get('/api/research/admin/chatter/feed/', { params }),
  getChatterVolume: (range, source, items) => API.get('/api/research/admin/chatter/volume/', { params: { range, source, ...(items ? { items } : {}) } }),
  // the page's picks (≤ 5 stocks / GICS groups, 't:NVDA,g:sub:45301020'): one line each, price only for a single pick
  getChatterCompare: (items, range = '3M') => API.get('/api/research/admin/chatter/company-series/', { params: { items, range } }),
  getChatterCompanySeries: (ticker, range = '3M') => API.get('/api/research/admin/chatter/company-series/', { params: { ticker, range } }),
  // Phase 1 deterministic narrative tracker — attention + favorability + momentum per entity.
  getNarrativeTracker: (params = {}) => API.get('/api/research/admin/tracker/', { params }),
  // §D deterministic co-occurrence relationship graph (buckets or entities) + explicit relations.
  getRelationshipGraph: (params = {}) => API.get('/api/research/admin/relationship-graph/', { params }),
  // Narrative propagation — private-company narrative routed onto covered tickers via graph edges
  // (deterministic; ranked watchlist + drivers + weekly sentiment time-series). Human-in-the-loop.
  getPropagationPrices: (tickers, cadence) => API.get('/api/research/admin/propagation/prices/', { params: { tickers: tickers.join(','), ...(cadence ? { cadence } : {}) } }),
  getPropagationTrajectories: () => API.get('/api/research/admin/propagation/trajectories/'),
  // Stored drivers + receipts for a rail lens (v2 | rail3 | rail3_novelty) — exact per-origin decomposition
  // of each ticker's score at the daily artifact's latest point; materialized with the trajectories.
  getPropagationAttribution: (rail, ticker) => API.get('/api/research/admin/propagation/attribution/', { params: { rail, ...(ticker ? { ticker } : {}) } }),
  // User-facing extraction summary for one video (the Signal receipts viewer — not the ops panel).
  getExtractionSummary: (docId) => API.get(`/api/research/extractions/${docId}/summary/`),
  // Generate + cache bounded top-N LLM edge labels (the only token-spending path; on-demand).
  labelRelationshipGraph: (body = {}) => API.post('/api/research/admin/relationship-graph/', body),
  // Relations Review (superuser) — paginated/filterable browse of ALL typed GraphRelation edges.
  // params: { relation, q, offset, limit }. Returns { total, relation_counts, rows }.
  getRelations: (params = {}) => API.get('/api/research/admin/relations/', { params }),
};
